/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IBuyboxAddToCartViewProps, IBuyboxAddToOrderTemplateViewProps, IBuyboxAddToWishlistViewProps, IBuyboxKeyInPriceViewProps, IBuyboxProductQuantityViewProps, IBuyboxShopSimilarLookViewProps } from './common/eshop-buybox-interface';
import { IBuyboxViewProps } from './eshop-buybox';
import { IBuyboxFindInStoreViewProps } from './components/eshop-buybox-find-in-store';
import { Overlay } from 'react-portal-overlay';
import {IBuyboxProductConfigureDropdownViewProps, IBuyboxProductConfigureViewProps} from './components/buyboxInterface';


const BuyboxView: React.FC<IBuyboxViewProps> = props => {
    const { ModuleProps, MediaGalleryContainerProps, ProductInfoContainerProps, addToCart, addToOrderTemplate, addToWishlist, configure, description, findInStore, quantity, price, title, rating, inventoryLabel, shopSimilarLook, keyInPrice,
        shopSimilarDescription, unitOfMeasure, orderSummaryContainerProps, /*moreProductDetails*/ } = props;

    return (
        <Module {...ModuleProps}>
            <Node {...MediaGalleryContainerProps}>
                {props.mediaGallery}
            </Node>
            <Node {...ProductInfoContainerProps}>
                {title}
                {price}
                {unitOfMeasure}
                {props.bulkPurchaseLink}
                {description}
                {rating}
                {configure && _renderConfigure(configure)}
                {keyInPrice && _renderKeyInPrice(keyInPrice)}
                {quantity && _renderQuantity(quantity)}
                {inventoryLabel}
                {addToCart && _renderAddToCart(addToCart)}
               { /*{moreProductDetails && _renderMoreProductDetails(props)}*/}
                {findInStore && _renderFindInStore(findInStore)}
                {addToOrderTemplate && _renderAddToOrderTemplate(addToOrderTemplate)}
                {addToWishlist && _renderAddToWishlist(addToWishlist)}
                {_renderSocialShare(props.slots && props.slots.socialShare)}
                {shopSimilarLook && _renderShopSimilarItem(shopSimilarLook)}
                {shopSimilarDescription && _renderShopSimilarItem(shopSimilarDescription)}
            </Node>
            
           <Node {...orderSummaryContainerProps}>
            <Overlay open={props.state.modalOpen==true?true:false}>
            <div className='eshop-overlay-content'>
           <div className= "eshop-order-confirm-summary">
           <div className="eshop-panel">
           <button className='eshop-ordersummary-close' onClick={()=> {_handleCloseButtonClick(props)}}></button>
           { props.eshopOrderSummary}
           </div>
           </div>
           </div>
           </Overlay>
            </Node>
        </Module>
    );
};
const _handleCloseButtonClick = (props:IBuyboxViewProps)=>{
    props.callbacks.changeModalOpen(false);
}
// const _handleCollapseButtonClick = (props:IBuyboxViewProps, value: boolean)=>{
//     props.callbacks.changeCollapseOpen(value);
// }

const _renderAddToCart = (addToCart: IBuyboxAddToCartViewProps): JSX.Element => {
    const { ContainerProps, errorBlock, button } = addToCart;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

// const _renderMoreProductDetails = (props: IBuyboxViewProps): JSX.Element => {
//     const { moreProductDetailsContainerProps, moreProductDetails } = props;
//     const showMoreProductDetails = props.state.isCollapseOpen;
//     const buttonClassName = showMoreProductDetails? 'more-product-details-close':'more-product-details-open';

//     return (
//         <Node {...moreProductDetailsContainerProps}>
//         <div className='ms-buybox__more-product-details-display_wrapper'>
//             <hr/>
//             <div className='more_product_details_title'>
//                 {props.config.moreProductDetailsTitle}
//                 {props.config.showCollapseButton && <button className={buttonClassName} onClick={()=> {_handleCollapseButtonClick(props,!showMoreProductDetails)}}></button>}
//             </div>
//            { showMoreProductDetails && <div className='more_product_details'>
//                   {moreProductDetails}
//              </div>
//           }
//            </div>
//     </Node>
//     );
// };

const _renderAddToOrderTemplate = (addToOrderTemplate: IBuyboxAddToOrderTemplateViewProps): JSX.Element => {
    const { ContainerProps, errorBlock, button } = addToOrderTemplate;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

const _renderAddToWishlist = (addToWishlist: IBuyboxAddToWishlistViewProps): JSX.Element => {
    const { ContainerProps, errorBlock, button } = addToWishlist;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

const _renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return (
        <Node {...ContainerProps}>
            {dropdowns.map(_renderConfigureDropdown)}
        </Node>
    );
};

const _renderSocialShare = (socialShare: React.ReactNode[]): JSX.Element | undefined => {
    if (!socialShare || socialShare.length === 0) {
        return undefined;
    }

    return (
        <>
            {socialShare[0]}
        </>
    );
};

const _renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select, errorContainerProps } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
            </Node>
            {select}
            <Node {...errorContainerProps}>
            {errors}
            </Node>
        </Node>
    );
};

const _renderFindInStore = (findInStore: IBuyboxFindInStoreViewProps): JSX.Element => {
    const { ContainerProps, storeSelector, heading, description, errors, button, modal, productPickupOptionList } = findInStore;

    return (
        <Node {...ContainerProps}>
            {storeSelector}
            {heading}
            {productPickupOptionList}
            {description}
            {errors}
            {button}
            {modal}
        </Node>
    );
};

const _renderQuantity = (quantity: IBuyboxProductQuantityViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input, errors, ErrorContainerProps } = quantity;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}  
            </Node>
            {input} 
            <Node {...ErrorContainerProps}> 
                {errors} 
            </Node> 
        </Node>
    );
};

const _renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
            </Node>
            {input}
        </Node>
    );
};

const _renderShopSimilarItem = (shopSimilarItem: IBuyboxShopSimilarLookViewProps): JSX.Element => {
    const { ContainerProps, errors, input } = shopSimilarItem;

    return (
        <Node {...ContainerProps}>
            {errors}
            {input}
        </Node>
    );
};
export default BuyboxView;
